import * as criteriaNames from "../../enums/criteriaNames";
import * as inputTypes from "../../enums/inputTypes";
import flatten from "lodash.flatten";
import findIndex from "lodash.findindex";
import * as immutable from "object-path-immutable";
import { isValid, parseISO, toDate } from "date-fns";
import some from "lodash.some";
import uniq from "lodash.uniq";
import uuid from "uuid";

function removeConsecutiveDuplicates(arr, elem) {
	if (!elem.id) {
		return arr;
	}
	if (!arr.length) {
		return [elem];
	}

	if (elem.id === arr[arr.length - 1].id) {
		return arr;
	}

	return [...arr, elem];
}

function sum(arr) {
	return arr.reduce((total, cur) => total + cur, 0);
}

export function cloneWithId(obj) {
	return { ...obj, __id: uuid.v4() };
}

export function getEmptyValue(type, criterionName) {
	if (
		type === inputTypes.TREE ||
		type === inputTypes.AUTOCOMPLETE ||
		type === inputTypes.LIST ||
		type === inputTypes.PFA ||
		type === inputTypes.EVENT_SIMPLE ||
		type === inputTypes.DFG
	) {
		return [];
	} else if (type === inputTypes.TERM_DEF_DESC) {
		let match = "is";
		if (criterionName === criteriaNames.TERM_DEFINITION) {
			match = "contains";
		} else if (criterionName === criteriaNames.FILING_TEXT) {
			match = "resembles";
		}

		return {
			match,
			strings: [],
		};
	} else if (type === inputTypes.DATE_RANGE) {
		return { after: "", before: "" };
	} else if (type === inputTypes.AGE_RANGE) {
		return { after: null, before: null };
	} else if (type === inputTypes.TEXT) {
		return { match: "boolean", query: "" };
	} else if (type === inputTypes.CRP) {
		return { reg_nos: [], strings: [] };
	} else if (type === inputTypes.TMP) {
		return { reg_nos: [], ser_nos: [], strings: [] };
	} else if (
		type === inputTypes.DOCUMENT ||
		type === inputTypes.STATIC ||
		type === inputTypes.SUB_SEARCH ||
		type === inputTypes.EVENT ||
		type === inputTypes.RADIO ||
		type === inputTypes.AT_LEAST
	) {
		return null;
	} else {
		throw new Error(`unsupported input type "${type}"`);
	}
}

export function isEmptyValue(value, type) {
	if (
		type === inputTypes.TREE ||
		type === inputTypes.AUTOCOMPLETE ||
		type === inputTypes.LIST ||
		type === inputTypes.PFA ||
		type === inputTypes.EVENT_SIMPLE
	) {
		return value.length === 0;
	} else if (type === inputTypes.TERM_DEF_DESC) {
		return value.strings.length === 0;
	} else if (type === inputTypes.DATE_RANGE || type === inputTypes.AGE_RANGE) {
		return !value.before && !value.after;
	} else if (type === inputTypes.TEXT) {
		return value.query === "";
	} else if (type === inputTypes.DFG) {
		return !some(
			value,
			(filter) =>
				filter.action.length > 0 ||
				filter.cause.length > 0 ||
				filter.result.length > 0 ||
				filter.issue.length > 0,
		);
	} else if (type === inputTypes.CRP) {
		return !value.reg_nos.length && !value.strings.length;
	} else if (type === inputTypes.TMP) {
		return !value.reg_nos.length && !value.ser_nos.length && !value.strings.length;
	} else if (type === inputTypes.RADIO) {
		return value === null || value === undefined;
	} else if (
		type === inputTypes.DOCUMENT ||
		type === inputTypes.STATIC ||
		type === inputTypes.SUB_SEARCH ||
		type === inputTypes.EVENT ||
		type === inputTypes.AT_LEAST
	) {
		return !value;
	} else {
		throw new Error(`unsupported input type "${type}"`);
	}
}

export function isDescribable(type) {
	return (
		type === inputTypes.TEXT ||
		type === inputTypes.DATE_RANGE ||
		type === inputTypes.AGE_RANGE ||
		type === inputTypes.TERM_DEF_DESC ||
		type === inputTypes.DOCUMENT ||
		type === inputTypes.STATIC ||
		type === inputTypes.SUB_SEARCH ||
		type === inputTypes.EVENT
	);
}

export function describeValue(value, type) {
	if (type === inputTypes.TEXT) {
		return `"${value.query}"`;
	} else if (type === inputTypes.DOCUMENT) {
		return "The Document";
	} else if (type === inputTypes.EVENT) {
		return "The Event";
	} else if (type === inputTypes.STATIC) {
		return (value && value.title) || "Static";
	} else if (type === inputTypes.AGE_RANGE) {
		if (value.before !== null && value.after !== null) {
			return `${value.after} - ${value.before} days old`;
		} else if (value.before !== null) {
			return `At most ${value.before} days old`;
		} else if (value.after !== null) {
			return `At least ${value.after} days old`;
		} else {
			return "";
		}
	} else if (type === inputTypes.DATE_RANGE) {
		if (value.after && value.before) {
			return `Between ${value.after} and ${value.before}`;
		} else if (value.after) {
			return `On or after ${value.after}`;
		} else if (value.before) {
			return `On or before ${value.before}`;
		} else {
			return "";
		}
	} else if (type === inputTypes.DFG) {
		const strings = [];
		if (value.length) {
			const filter = value[0];
			if (filter.cause.length) {
				strings.push(`${filter.cause.length} Posture(s) of Motion`);
			}

			if (filter.action.length) {
				strings.push(`${filter.action.length} Type(s) of Document`);
			}

			if (filter.result.length) {
				strings.push(`${filter.result.length} Result(s) of Motion`);
			}

			let issueSum = 0;
			if (filter.issue.length) {
				filter.issue.forEach((arr) => {
					issueSum += arr.length;
				});
				strings.push(`${issueSum} Legal Issue(s)`);
			}
		}

		return strings.join(" | ");
	} else if (type === inputTypes.TERM_DEF_DESC) {
		return value.strings.map((x) => `"${x}"`).join(" | ");
	} else if (type === inputTypes.SUB_SEARCH) {
		return value.title;
	} else {
		throw new Error(`unsupported input type "${type}"`);
	}
}

export function getJoinpathTitle(targetJoinpaths, criterionName, joinpathName) {
	if (criterionName === criteriaNames.EVENT_ACTION) {
		return "Document Type";
	} else if (criterionName === criteriaNames.EVENT_CAUSE) {
		return "Posture of Motion";
	} else if (criterionName === criteriaNames.EVENT_RESULT) {
		return "Result of Motion";
	} else if (criterionName === criteriaNames.EVENT_ISSUE) {
		return "Legal Issue";
	}

	const [joinpath] = targetJoinpaths.filter(
		(x) => x.name === joinpathName && x.criterion_name === criterionName,
	);

	if (joinpath) {
		return joinpath.title;
	}

	return criterionName;
}

export function joinpathsForCriterion(availableJoinpaths, criterionName, joinpathName) {
	let selectedJoinpath;
	const allJoinpaths = availableJoinpaths
		.filter((j) => j.criterion_name === criterionName)
		.map((j) => {
			if (joinpathName && j.name === joinpathName) {
				selectedJoinpath = j;
			}

			return j;
		});

	if (allJoinpaths.length === 0) {
		throw new Error(`joinpath not found for criterion "${criterionName}"`);
	}

	if (!selectedJoinpath) {
		selectedJoinpath = allJoinpaths[0];
	}

	return {
		all: allJoinpaths,
		selected: selectedJoinpath,
	};
}

export function apiCriteriaFromRedux(tabs, joinpaths, criteria) {
	const criterionIdsByTab = tabs.map((t) => {
		const columnCriterionIds = flatten(t.criterionIdsDesktop);
		return t.criterionIdsFullWidth.concat(columnCriterionIds);
	});
	return flatten(criterionIdsByTab)
		.map((id) => criteria[id])
		.filter((criterion) => {
			return (
				!criterion.hasValidationError &&
				!criterion.validating &&
				!isEmptyValue(criterion.value, criterion.type)
			);
		})
		.map((criterion) => {
			const joinpath = joinpaths[criterion.selectedJoinpathId];
			return {
				joinpath_name: joinpath.name,
				name: criterion.name,
				value: criterion.value,
			};
		});
}

export function getEditorHeight(inputType) {
	if (inputType === inputTypes.DFG) {
		return 3;
	}
	if (inputType === inputTypes.TEXT) {
		return 2;
	}
	return 1;
}

// NOTE: data should be an array of objects with an id and height where a "tall" element takes up
// multiple, contiguous positions.
// EX:
// [
// 	{ id: "short-1", height: 1 },
// 	{ id: "short-2", height: 1 },
// 	{ id: "tall", height: 3 },
// 	{ id: "tall", height: 3 },
// 	{ id: "tall", height: 3 },
// 	{ id: "short-3", height: 1 },
// ]
export function getThreeColumnLayout(data) {
	let columnOneLastElem = 0;
	let columnTwoLastElem = 0;

	let minColHeight = data.length;
	let minColIndexSum = 3;

	for (let i = 0; i <= data.length - 2; i++) {
		if (i < data.length - 2 && data[i].id === data[i + 1].id) {
			continue;
		}

		for (let j = i + 1; j <= data.length - 1; j++) {
			if (j < data.length - 1 && data[j].id === data[j + 1].id) {
				continue;
			}

			const columnHeights = [i + 1, j - i, data.length - j - 1];
			const tallestColumn = Math.max(...columnHeights);
			const tallestColumnIndexSum = sum(
				columnHeights.map((h, index) => {
					if (h === tallestColumn) {
						return index;
					}
					return 0;
				}),
			);

			if (
				tallestColumn < minColHeight ||
				(tallestColumn === minColHeight && tallestColumnIndexSum < minColIndexSum)
			) {
				minColHeight = tallestColumn;
				minColIndexSum = tallestColumnIndexSum;
				columnOneLastElem = i;
				columnTwoLastElem = j;
			}
		}
	}

	const columnOneBreak = columnOneLastElem + 1;
	const columnTwoBreak = columnTwoLastElem + 1;

	const columnOne = data.slice(0, columnOneBreak).reduce(removeConsecutiveDuplicates, []);
	const columnTwo = data
		.slice(columnOneBreak, columnTwoBreak)
		.reduce(removeConsecutiveDuplicates, []);
	const columnThree = data.slice(columnTwoBreak).reduce(removeConsecutiveDuplicates, []);

	const ids = [
		columnOne.map((c) => c.id),
		columnTwo.map((c) => c.id),
		columnThree.map((c) => c.id),
	];

	const heights = [
		columnOne.map((c) => c.height),
		columnTwo.map((c) => c.height),
		columnThree.map((c) => c.height),
	];

	return { ids, heights };
}

// NOTE: data should be an array of objects with an id and height where a "tall" element takes up
// multiple, contiguous positions.
// EX:
// [
// 	{ id: "short-1", height: 1 },
// 	{ id: "short-2", height: 1 },
// 	{ id: "tall", height: 3 },
// 	{ id: "tall", height: 3 },
// 	{ id: "tall", height: 3 },
// 	{ id: "short-3", height: 1 },
// ]
export function getTwoColumnLayout(data) {
	let columnOneLastElem = 0;

	let minColHeight = data.length;

	for (let i = 0; i <= data.length - 1; i++) {
		if (i < data.length - 1 && data[i].id === data[i + 1].id) {
			continue;
		}

		const columnHeights = [i + 1, data.length - (i + 1)];
		const tallestColumn = Math.max(...columnHeights);

		if (
			tallestColumn < minColHeight ||
			(tallestColumn === minColHeight && tallestColumn === columnHeights[0])
		) {
			minColHeight = tallestColumn;
			columnOneLastElem = i;
		}
	}

	const columnOneBreak = columnOneLastElem + 1;

	const columnOne = data.slice(0, columnOneBreak).reduce(removeConsecutiveDuplicates, []);
	const columnTwo = data.slice(columnOneBreak).reduce(removeConsecutiveDuplicates, []);

	const ids = [columnOne.map((c) => c.id), columnTwo.map((c) => c.id)];

	const heights = [columnOne.map((c) => c.height), columnTwo.map((c) => c.height)];

	return { ids, heights };
}

export function markToRemove(search, path) {
	if (typeof path !== "string") {
		return search;
	}

	// NOTE: We are transforming the path string from what the API provides to what the object-path-
	// immutable library is expecting. For example: "criteria[1]" -> "criteria.1"
	const finalPath = path.replace(/\[(\d+)\]/g, ".$1");
	return immutable.set(search, finalPath, { shouldRemove: true });
}

export function removeMarked(search) {
	if (!search || !search.criteria.length) {
		return search;
	}

	return {
		...search,
		criteria: search.criteria
			.map((criterion) => {
				if (!criterion) {
					return criterion;
				}

				return criterion.shouldRemove
					? null
					: {
							...criterion,
							value: removeMarkedValue(criterion.value),
						};
			})
			.filter((criterion) => criterion !== null),
	};
}

function removeMarkedValue(value) {
	if (value === null || value === undefined) {
		return value;
	}

	if (value.shouldRemove) {
		return null;
	}

	if (Array.isArray(value) && value.length) {
		return value.map((val) => removeMarkedValue(val)).filter((val) => val !== null);
	}

	if (typeof value === "object" && Object.keys(value).length) {
		const keys = Object.keys(value);

		const nextValue = {};
		keys.forEach((key) => {
			nextValue[key] = removeMarkedValue(value[key]);
		});
		return nextValue;
	}

	return value;
}

export function getDateFromAPIString(dateString) {
	if (!dateString) {
		return undefined;
	}

	const split = dateString.split("-").map((token) => {
		if (token.length <= 1) {
			return `0${token}`;
		}
		return token;
	});
	const isoString = split.join("-");

	const isoDate = parseISO(isoString);
	const date = toDate(isoDate);
	if (!isValid(date)) {
		return "";
	}

	return date;
}

export function combineValuesOr(value1, value2, type, criterionName) {
	if (
		type === inputTypes.TREE ||
		type === inputTypes.AUTOCOMPLETE ||
		type === inputTypes.LIST ||
		type === inputTypes.PFA ||
		type === inputTypes.EVENT_SIMPLE
	) {
		return [...value1, ...value2];
	} else if (type === inputTypes.DFG) {
		const allValues = [...value1, ...value2];
		const combined = combineObjectArrsNoDuplicates(allValues);
		return [
			{
				cause: [],
				action: [],
				result: [],
				...combined,
				issue:
					combined && combined.issue && combined.issue.length
						? [flatten(combined.issue)]
						: [],
			},
		];
	} else if (type === inputTypes.TERM_DEF_DESC) {
		let match = "is";
		if (criterionName === criteriaNames.TERM_DEFINITION) {
			match = "contains";
		} else if (criterionName === criteriaNames.FILING_TEXT) {
			match = "resembles";
		}

		return {
			match,
			strings: [...value1.strings, ...value2.strings],
		};
	} else if (type === inputTypes.TEXT) {
		let query = "";
		if (value1.query) {
			query += value1.query;
		}
		if (value2.query) {
			query = `${query} ${value2.query}`;
		}
		return { match: "natural", query };
	} else if (type === inputTypes.CRP) {
		const combined = combineObjectArrsNoDuplicates([value1, value2]);
		return {
			reg_nos: [],
			strings: [],
			...combined,
		};
	} else if (type === inputTypes.TMP) {
		const combined = combineObjectArrsNoDuplicates([value1, value2]);
		return {
			reg_nos: [],
			ser_nos: [],
			strings: [],
			...combined,
		};
	} else if (
		type === inputTypes.DATE_RANGE ||
		type === inputTypes.AGE_RANGE ||
		type === inputTypes.AT_LEAST ||
		type === inputTypes.DOCUMENT ||
		type === inputTypes.STATIC ||
		type === inputTypes.SUB_SEARCH ||
		type === inputTypes.EVENT ||
		type === inputTypes.RADIO
	) {
		return null;
	} else {
		throw new Error(`cannot OR vales for criteria of editor "${type}"`);
	}
}

function combineObjectArrsNoDuplicates(arr) {
	const finalValue = {};
	if (!arr.length) {
		return null;
	}
	arr.forEach((curValue) => {
		const curValueKeys = Object.keys(curValue);
		curValueKeys.forEach((key) => {
			const curKeyArr = curValue[key];
			if (!Array.isArray(curKeyArr) || !curKeyArr.length) {
				return;
			}
			finalValue[key] = finalValue[key] ? [...finalValue[key], ...curKeyArr] : [...curKeyArr];
		});
	});

	const finalValueNoDups = {};
	const finalValueKeys = Object.keys(finalValue);
	finalValueKeys.forEach((key) => {
		const values = finalValue[key];
		finalValueNoDups[key] = removeDuplicates(values);
	});
	return finalValueNoDups;
}

function removeDuplicates(arr) {
	if (!Array.isArray(arr)) {
		return arr;
	}
	const valuesObj = {};
	let invalidValueFound = false;
	arr.forEach((val) => {
		if (Number.isFinite(val)) {
			valuesObj[val] = val;
			return;
		}
		if (typeof val === "string") {
			valuesObj[val] = val;
			return;
		}
		invalidValueFound = true;
	});
	return invalidValueFound ? arr : Object.keys(valuesObj).map((k) => valuesObj[k]);
}

function removeCriteriaNames(names, criteria) {
	return criteria.filter((c) => !names.some((removeName) => c.name === removeName));
}

export function addFilterToCriteria(criteria, filter) {
	if (filter.add_criteria) {
		const nextCriteria =
			filter.remove_criterion_name && filter.remove_criterion_name.length
				? removeCriteriaNames(filter.remove_criterion_name, criteria)
				: [...criteria];

		return nextCriteria.concat(filter.add_criteria);
	} else {
		throw new Error(`cannot apply filter ${JSON.stringify(filter)}`);
	}
}

function getCommonNameForCriteria(editors, criteria) {
	if (!criteria || criteria.length === 0) {
		return undefined;
	}

	if (criteria.length === 1) {
		return criteria[0].name;
	}

	const initialCriterionName = criteria[0].name;
	if (!initialCriterionName) {
		return undefined;
	}

	return criteria.some((c) => c.name !== initialCriterionName) ? undefined : initialCriterionName;
}

export function getCommonEditorForCriteria(editors, ...criteria) {
	if (!criteria || criteria.length < 2 || uniq(criteria.map((c) => c.name)).length !== 1) {
		return undefined;
	}

	const [initialCriterion] = criteria;
	return editors[initialCriterion.name];
}

export function getCommonEditorForFilters(editors, filters) {
	if (
		!filters ||
		filters.length === 0 ||
		!filters[0].add_criteria ||
		!filters[0].add_criteria.length
	) {
		return undefined;
	}

	let initialCriterionName = filters[0].add_criteria[0].name;
	if (!initialCriterionName) {
		return undefined;
	}

	filters.forEach((f) => {
		if (f.add_criteria && f.add_criteria.length) {
			const commonCriterionName = getCommonNameForCriteria(editors, f.add_criteria);
			if (!commonCriterionName || commonCriterionName !== initialCriterionName) {
				initialCriterionName = undefined;
			}
		}
	});

	return initialCriterionName ? editors[initialCriterionName] : undefined;
}

function combineCriteria(criteria, commonEditorType) {
	if (!criteria.length || !commonEditorType) {
		return undefined;
	}
	if (criteria.length === 1) {
		return criteria[0];
	}
	const [firstCriterion, ...remainingCriteria] = criteria;
	return remainingCriteria.reduce(
		(acc, current) => {
			const combinedValue = combineValuesOr(
				acc.value,
				current.value,
				commonEditorType,
				current.name,
			);

			return combinedValue ? { ...current, value: combinedValue } : acc;
		},
		{ ...firstCriterion },
	);
}

export function addCommonFiltersToCriteria(criteria, filters, editors) {
	if (!filters.length) {
		return criteria;
	}

	if (filters.length === 1) {
		return addFilterToCriteria(criteria, filters[0]);
	}

	const criterionEditor = getCommonEditorForFilters(editors, filters);
	if (!criterionEditor) {
		return criteria;
	}

	const [firstFilter, ...remainingFilters] = filters;
	const joinedFilter = remainingFilters.reduce(
		(accFilter, currentFilter) => {
			const accCriterion = combineCriteria(accFilter.add_criteria, criterionEditor.type);
			const currentCriterion = combineCriteria(
				currentFilter.add_criteria,
				criterionEditor.type,
			);
			const combinedValue = combineValuesOr(
				accCriterion.value,
				currentCriterion.value,
				criterionEditor.type,
				currentCriterion.name,
			);

			const removeNames =
				accFilter.remove_criterion_name || currentFilter.remove_criterion_name
					? uniq([
							...accFilter.remove_criterion_name,
							...currentFilter.remove_criterion_name,
						])
					: null;

			const nextAccFilter = combinedValue || removeNames ? { ...currentFilter } : accFilter;

			if (combinedValue) {
				nextAccFilter.add_criteria = [{ ...currentCriterion, value: combinedValue }];
			}
			if (removeNames) {
				nextAccFilter.remove_criterion_name = removeNames;
			}

			return nextAccFilter;
		},
		{ ...firstFilter },
	);

	let nextCriteria = [...criteria];
	filters.forEach((filter) => {
		if (filter.remove_criterion_name && filter.remove_criterion_name.length) {
			nextCriteria = nextCriteria.filter((c) => c.name !== filter.remove_criterion_name);
		}
	});
	return addFilterToCriteria(nextCriteria, joinedFilter);
}

export function addFiltersToCriteria(criteria, filters, editors) {
	if (!filters.length) {
		return criteria;
	}

	if (filters.length === 1) {
		return addFilterToCriteria(criteria, filters[0]);
	}

	const removeNames = uniq(
		flatten(filters.map((f) => f.remove_criterion_name)).filter((f) => !!f),
	);
	let nextCriteria = removeCriteriaNames(removeNames, criteria);

	const addCriteria = flatten(filters.map((f) => f.add_criteria));
	addCriteria.forEach((addCriterion) => {
		const matchIdx = findIndex(
			nextCriteria,
			(c) => c.name === addCriterion.name && c.joinpath_name === addCriterion.joinpath_name,
		);
		if (matchIdx === -1) {
			nextCriteria = [...nextCriteria, addCriterion];
		} else {
			const existingCriterion = nextCriteria[matchIdx];
			const editor = getCommonEditorForCriteria(editors, existingCriterion, addCriterion);
			const value = combineValuesOr(
				existingCriterion.value,
				addCriterion.value,
				editor.type,
				addCriterion.name,
			);
			nextCriteria = [
				...nextCriteria.slice(0, matchIdx),
				{ ...existingCriterion, value },
				...nextCriteria.slice(matchIdx + 1),
			];
		}
	});

	return nextCriteria;
}

export function getSearchTabCriterionIds(tab) {
	return tab.criterionIdsFullWidth.concat(flatten(tab.criterionIdsDesktop));
}

export function getSearchTabsCriterionIds(tabs) {
	return flatten(tabs.map((t) => getSearchTabCriterionIds(t)));
}
